/**
 * Content for the Integrations preview at /demo/integrations. Every feed is
 * invented — the page is served unauthenticated.
 *
 * Datetimes are derived from "now": a monitoring table whose last run is
 * stamped last quarter says the opposite of what the feature is for.
 */

// The same zones the signed-in Integrations Overview offers.
export const DEMO_INTEGRATION_TIMEZONES = ['UTC', 'EST', 'CST', 'PST'] as const

export type DemoIntegrationTimezone = (typeof DEMO_INTEGRATION_TIMEZONES)[number]

export type DemoIntegrationStatus = 'On time' | 'Disrupt'

export type DemoIntegration = {
  id: string
  name: string
  type: 'SFTP' | 'Email' | 'Spreadsheet'
  usage: string
  cronDescription: string
  status: DemoIntegrationStatus
  trigger: Date
  // Trigger plus the run's usual duration — the time it has to clear.
  deadline: Date
  lastRun: Date
}

type DemoIntegrationSeed = Omit<DemoIntegration, 'trigger' | 'deadline' | 'lastRun'> & {
  // 24h HH:MM, UTC.
  triggerTimeUtc: string
  runtimeMinutes: number
  lastRunHoursAgo: number
}

const SEEDS: Array<DemoIntegrationSeed> = [
  {
    id: 'tms-load-tenders',
    name: 'TMS load tenders',
    type: 'SFTP',
    usage: 'Loads, tenders and status events',
    cronDescription: 'Every 4 hours',
    triggerTimeUtc: '04:00',
    runtimeMinutes: 35,
    lastRunHoursAgo: 3,
    status: 'On time',
  },
  {
    id: 'carrier-rate-confirmations',
    name: 'Carrier rate confirmations',
    type: 'SFTP',
    usage: 'Contracted rates and lane pricing',
    cronDescription: 'Every day at 05:30',
    triggerTimeUtc: '05:30',
    runtimeMinutes: 45,
    lastRunHoursAgo: 6,
    status: 'On time',
  },
  {
    id: 'dock-appointments-export',
    name: 'Dock appointment export',
    type: 'Email',
    usage: 'Appointments, dwell and check-in times',
    cronDescription: 'Weekdays at 06:00',
    triggerTimeUtc: '06:00',
    runtimeMinutes: 25,
    lastRunHoursAgo: 5,
    status: 'On time',
  },
  {
    id: 'accessorials-workbook',
    name: 'Accessorials workbook',
    type: 'Spreadsheet',
    usage: 'Detention and accessorial charges',
    cronDescription: 'Mondays at 07:00',
    triggerTimeUtc: '07:00',
    runtimeMinutes: 20,
    lastRunHoursAgo: 27,
    status: 'Disrupt',
  },
  {
    id: 'facility-reference-data',
    name: 'Facility reference data',
    type: 'SFTP',
    usage: 'Warehouse, dock and door master data',
    cronDescription: 'Every day at 03:15',
    triggerTimeUtc: '03:15',
    runtimeMinutes: 15,
    lastRunHoursAgo: 9,
    status: 'On time',
  },
]

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS

const LAST_REFRESH_MINUTES_AGO = 12

function atUtcTime(now: Date, time: string): Date {
  const [hours, minutes] = time.split(':').map(Number)

  return new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hours, minutes)
  )
}

export function buildDemoIntegrations(now: Date): Array<DemoIntegration> {
  return SEEDS.map(({ triggerTimeUtc, runtimeMinutes, lastRunHoursAgo, ...integration }) => {
    const trigger = atUtcTime(now, triggerTimeUtc)

    return {
      ...integration,
      trigger,
      deadline: new Date(trigger.getTime() + runtimeMinutes * MINUTE_MS),
      lastRun: new Date(now.getTime() - lastRunHoursAgo * HOUR_MS),
    }
  })
}

export function demoIntegrationsLastRefresh(now: Date): Date {
  return new Date(now.getTime() - LAST_REFRESH_MINUTES_AGO * MINUTE_MS)
}

// Same shape as the signed-in overview, so the table is recognisable later.
export function formatDemoIntegrationDateTime(date: Date, timeZone: DemoIntegrationTimezone) {
  return date
    .toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    })
    .replace(/[‎‏]/g, '')
    .replace(/\//g, '-')
}

export const DEMO_INTEGRATION_HIGHLIGHTS = [
  {
    title: 'Is today’s data actually in?',
    body: 'Each feed shows its last successful run beside the time it was due. You know whether a report is built on today’s numbers before you take it into a meeting.',
  },
  {
    title: 'Deadlines, not just schedules',
    body: 'A feed that starts on time can still finish late. Every integration carries the deadline its run has to clear, so a slow load surfaces as a risk instead of a surprise.',
  },
  {
    title: 'However the data arrives',
    body: 'SFTP drops, emailed exports and shared spreadsheets are monitored the same way — the partner who still sends a file by hand is as visible as a nightly system feed.',
  },
  {
    title: 'Disrupt means we already know',
    body: 'The same signal that turns a row red pages our data team. You are not the monitoring: by the time you notice, someone is already on it.',
  },
]
